import { EventEmitter } from "@angular/core";
import { KeyFunctionMap, RxCollection, RxDocument, RxGraphQLReplicationQueryBuilder, RxJsonSchema, RxQuery, RxReplicationState, SyncOptions, SyncOptionsGraphQL } from "rxdb";
import { RxCollectionCreatorBase } from "rxdb/dist/types/types";
import { RxGraphQLReplicationState } from 'rxdb/plugins/replication-graphql';
import { BehaviorSubject, Subject, timer } from "rxjs";

import { AppState } from "../../app-state";
import { TestbedRxdbState } from "./testbed-rxdb-state";
import { TestbedCollectionService } from "./testbed-collection.service"
export interface ReplicationStateListener {
    replicationStateCreated(replicationState:RxReplicationState|RxGraphQLReplicationState) : void;
    replicationStateCanceled(replicationState:RxReplicationState|RxGraphQLReplicationState) : void;
}


class SyncConfig {
    push : boolean
    pull : boolean
    intervalMs : number
    retry : boolean
    name? : string
    query? : ((c : RxCollection) => RxQuery) | RxGraphQLReplicationQueryBuilder
    pushQuery? : RxGraphQLReplicationQueryBuilder
    bGraphQL?: boolean
    graphQLSubscription?: string // needed for live syncs (interval==0)
}

// base class for all couchdb and graphql rxdb collections
export abstract class TestbedRxdbCollectionService<ModelType,DocumentType=RxDocument<ModelType,{}>> 
    extends TestbedCollectionService<DocumentType> {

    static readonly MAX_LISTENERS = 20

    rxCollection : RxCollection<DocumentType>

    syncConfig : SyncConfig
    
    replicationState? : RxReplicationState | RxGraphQLReplicationState
    bActiveSync?: boolean
    httpStatusCode?: number
    bStopped?: boolean
    replicationStateStale?: Subject<void>
    replicationStateListeners?: ReplicationStateListener[] = []
    
    initialReplicationComplete$ = new BehaviorSubject<boolean>(false)
    replicationActive$ = new EventEmitter<boolean>()

    migrationStrategies : { [migrationId:number]:(docData:ModelType)=>ModelType } = {
        1: (docData) => docData
    }

    methods : KeyFunctionMap

    public error$ = new EventEmitter<any>()
    
    constructor( 
            public name : string, 
            public schema : RxJsonSchema<any>,
            appState: AppState,
            protected rxdbState: TestbedRxdbState ) {
                super(appState)
    }

    // CouchDB and GraphQL collection service base classes implement these
    abstract startSync() : Promise<void>;
    abstract doLiveSync() : Promise<void>;        
    abstract doNonLiveSync() : Promise<void>;        
    abstract syncNow() : void;

    
    isRemoteCollectionDown() {
        return [504,-1].includes(this.httpStatusCode)
    }

    isRemoteCollectionUnauthorized() : boolean {
        return this.httpStatusCode==401
    }

    buildRxdbConfig() : RxCollectionCreatorBase {
        let config : any = { 
            name: this.name, 
            schema: this.schema
        }
        if ( this.methods ) {
            config.methods = this.methods
        }
        if ( this.migrationStrategies ) {
            config.migrationStrategies = this.migrationStrategies
        }
        return config
    }
    

    addReplicationStateListener( listener : ReplicationStateListener ) {
        this.replicationStateListeners.push(listener)
    }


    resetReplicationStaleSubject() {
        this.replicationStateStale?.complete()
        this.replicationStateStale = new Subject<void>()
    }

    scheduleNonLiveSync = async ( delayMs : number ) => {
        this.replicationStateStale.next()
        timer(0).subscribe( async ms => {
            try{
                this.replicationStateListeners?.forEach(listener=>{
                    listener?.replicationStateCanceled(this.replicationState)
                })
                await this.replicationState.cancel()
                timer(delayMs).subscribe(async ms=>{
                    if ( !this.bStopped ) {
                        this.doNonLiveSync()
                    }
                })
            } catch(error ) {
                console.error(error)
            }
        })
    }


    getAll = async (): Promise<DocumentType[]> => {
        let query = this.rxCollection.find()
        return await query.exec()
    }

    async getById( id : string ): Promise<DocumentType> {
        if ( !id ) {
            throw new Error(`${this.rxCollection.name} getById requires non-empty id`)
        }
        let query = this.rxCollection.findOne(id)
        return await query.exec()
    }

    getByIds = async ( ids : string[] ): Promise<RxDocument<DocumentType>[]> => {
        /*
        //TBD - this is still very slow so using Promise.all for now
        let query = this.rxCollection.find().where('_id').in(ids)
        const result = await query.exec()
        */
        
        let promises : Promise<RxDocument<DocumentType>>[] = [];

        for ( const id of ids ) {
            promises.push(this.rxCollection.findOne(id).exec())
        }

        const resultDocs = await Promise.all(promises)
        return resultDocs
    }

    // workaround for rxCollection.find().where().in() crashing
    findByAttrIn = async ( attrName: string, values : string[] ): Promise<RxDocument<DocumentType>[]> => {
        let promises : Promise<RxDocument<DocumentType>[]>[] = [];
        for ( const value of values ) {
            const selector = {};
            selector[attrName] = value;
            promises.push(this.rxCollection.find({selector}).exec());
        }
        const results = await Promise.all(promises);
        return results.flat();
    }



    isReady = () => this.rxCollection != null
    

    updateRxdbStateAfterHttpFetch(oldOkCnt:number) {
        const okCnt = this.rxdbState.getOkCount()
        this.rxdbState.updateAllOk()
        if ( this.rxdbState.bAllOk ) {
            //console.log('all ok')
            this.rxdbState.httpRespWarningMsg = ''
        } else {
            if ( okCnt < oldOkCnt ) {
                if ( this.appState.isOffline() ) {
                    //console.log('offline and ok count getting worse')
                    this.rxdbState.httpRespWarningMsg = ""
                } else {
                    //console.log('online and ok count getting worse')
                    if ( this.rxdbState.someRemoteCollectionsUnauthorized()  ) {
                        this.rxdbState.httpRespWarningMsg = "Database login failed"
                    } else {
                        this.rxdbState.httpRespWarningMsg = ""
                    }
                }
            } else if ( okCnt > oldOkCnt ) {
                //console.log('ok count getting better')
                this.rxdbState.httpRespWarningMsg = ''
            } else {
                //console.log('ok count same: ' + okCnt)
                if ( okCnt == 0 ) {
                    if ( this.rxdbState.everyRemoteCollectionDown() ) {
                        this.rxdbState.httpRespWarningMsg = "offline"
                    } else {
                        this.rxdbState.httpRespWarningMsg = ""
                    }
                }
                // say nothing new because too hard to figure out what to say here!!!!
            }
        }
    }
}